import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "add-intent-modal" }
const _hoisted_2 = { class: "edit-title-container" }
const _hoisted_3 = { class: "field" }
const _hoisted_4 = { class: "edit-utterances-container" }

import Modal from '@/shared/components/Modal.vue';
import { nanoid } from 'nanoid';
import { required, maxLength } from '@vuelidate/validators';
import VuelidateUtil from '@/shared/util/vuelidateUtil';
import Toast from '@/shared/services/toast';
import { IntentViewModel } from '@/features/bots/models/intents/intentViewModel';
import { UtteranceViewModel } from '@/features/bots/models/intents/utteranceViewModel';
import UtteranceList from '@/features/bots/components/designer/UtteranceList.vue';
import { CREATE_INTENT } from '@/store/intents/actionTypes';
import { SaveIntentViewModel } from '../../models/intents/saveIntentViewModel';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';


export default /*@__PURE__*/_defineComponent({
  __name: 'AddIntentModal',
  emits: ["completed", "closed"],
  setup(__props, { expose: __expose, emit: __emit }) {

__expose({
  addNewIntent,
});

const store = useStore();
const createIntent = (intent: SaveIntentViewModel): Promise<IntentViewModel> => store.dispatch(CREATE_INTENT, intent);

const isSaveInProgress = ref(false);
const intent = ref<IntentViewModel>({} as IntentViewModel);
const intentId = ref('');
const intentTitle = ref('');
const intentUtterances = ref<UtteranceViewModel[]>([]);

const validations = {
  intentTitle: {
    required,
    maxLength: maxLength(50),
    intentNameValid: VuelidateUtil.intentName,
  },
  intentUtterances: {
    required,
  },
};

const $v = useVuelidate(validations, { intentTitle, intentUtterances });

const emit = __emit;

function cancel() {
  $v.value.$reset();
  emit('closed');
}

async function saveIntent() {
  $v.value.$touch();
  if ($v.value.$invalid) {
    Toast.showError('Fix the errors in red before saving');
    return;
  }
  try {
    isSaveInProgress.value = true;
    intent.value.utterances = intentUtterances.value.sort((uttOne, uttTwo) => uttOne.text.localeCompare(uttTwo.text));
    intent.value.title = intentTitle.value.trim();
    intent.value = await createIntent({
      title: intent.value.title,
      utterances: intent.value.utterances.map((utterance) => utterance.text),
    });
    Toast.showSuccess('Saved intent');
    emit('completed', intent.value);
    $v.value.$reset();
  } catch (ex: any) {
    Toast.showError(ex.message);
  } finally {
    isSaveInProgress.value = false;
  }
}

function addNewIntent(firstUtterance?: string) {
  intent.value = {
    id: `intent-${nanoid(7)}`,
    title: '',
    utterances: [{ id: nanoid(7), text: firstUtterance ? firstUtterance : '' }],
  } as IntentViewModel;

  intentId.value = intent.value.id;
  intentTitle.value = intent.value.title;
  intentUtterances.value = intent.value.utterances;
}

const errorMessage = computed(() => {
  const intentTitle = $v.value.intentTitle;
  if (!intentTitle.$dirty) {
    return '';
  }
  if (intentTitle.required.$invalid) {
    return 'Name must not be empty';
  } else if (intentTitle.intentNameValid.$invalid) {
    return 'Name must not contain <, >, *, %, &, :, \\, $';
  } else if (intentTitle.maxLength.$invalid) {
    return 'Name must not be longer than 50 characters';
  }

  return '';
});

return (_ctx: any,_cache: any) => {
  const _component_n_text_input = _resolveComponent("n-text-input")!
  const _component_n_button = _resolveComponent("n-button")!
  const _component_n_button_group = _resolveComponent("n-button-group")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Modal, {
      onCancel: _cache[3] || (_cache[3] = ($event: any) => (cancel()))
    }, {
      title: _withCtx(() => _cache[4] || (_cache[4] = [
        _createElementVNode("p", { class: "modal-card-title" }, "Add a new intent", -1)
      ])),
      body: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _cache[5] || (_cache[5] = _createElementVNode("h1", null, "Name", -1)),
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_n_text_input, {
              class: "edit-title",
              modelValue: intentTitle.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((intentTitle).value = $event)),
              hideLabel: true,
              label: "Intent name",
              disabled: isSaveInProgress.value,
              placeholder: "Enter Intent Name",
              errorMessage: errorMessage.value,
              onInput: _cache[1] || (_cache[1] = ($event: any) => (_unref($v).intentTitle.$touch()))
            }, null, 8, ["modelValue", "disabled", "errorMessage"])
          ])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _cache[6] || (_cache[6] = _createElementVNode("h1", null, "Example Utterances", -1)),
          _createVNode(UtteranceList, {
            modelValue: intentUtterances.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((intentUtterances).value = $event)),
            term: "utterance",
            isEditable: !isSaveInProgress.value
          }, null, 8, ["modelValue", "isEditable"])
        ])
      ]),
      buttons: _withCtx(() => [
        _createVNode(_component_n_button_group, null, {
          default: _withCtx(() => [
            _createVNode(_component_n_button, {
              variant: "primary",
              onClick: saveIntent,
              loading: isSaveInProgress.value
            }, {
              default: _withCtx(() => _cache[7] || (_cache[7] = [
                _createTextVNode("Save ")
              ])),
              _: 1
            }, 8, ["loading"]),
            _createVNode(_component_n_button, {
              onClick: cancel,
              disabled: isSaveInProgress.value,
              variant: "primary--outline"
            }, {
              default: _withCtx(() => _cache[8] || (_cache[8] = [
                _createTextVNode("Cancel")
              ])),
              _: 1
            }, 8, ["disabled"])
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})